import React, { useEffect, useState } from "react";
import { parseLocalDate, todayString } from "../../utils/dateUtils";
import { WEEKDAYS, describeRecurrence, formatDueDate } from "./taskUtils";

const UNITS = { daily: "day", weekly: "week", monthly: "month" };
const WORKDAYS = [1, 2, 3, 4, 5];

// Value of the frequency <select> for a stored rule.
function presetOf(rule) {
  if (!rule) return "none";
  if (
    rule.freq === "weekly" &&
    rule.interval === 1 &&
    rule.weekdays.length === WORKDAYS.length &&
    WORKDAYS.every((d) => rule.weekdays.includes(d))
  ) {
    return "weekdays";
  }
  return rule.freq;
}

// Small ↻ marker next to a repeating task's due date in lists and boards.
export function RepeatBadge({ rule }) {
  const label = `Repeats: ${describeRecurrence(rule)}`;
  return (
    <span className="tk-repeat-badge" title={label} aria-label={label}>
      ↻
    </span>
  );
}

// The "Repeat" row of the task detail pane. Completing a repeating task
// creates its next occurrence on the backend; this only edits the rule.
export default function RepeatField({ task, onSave, onOpenTask }) {
  const rule = task.recurrence;
  const [intervalDraft, setIntervalDraft] = useState(String(rule?.interval || 1));
  useEffect(() => setIntervalDraft(String(rule?.interval || 1)), [rule?.interval]);

  // The backend moves the due date onto the pattern after every change.
  const save = (next) => onSave({ recurrence: next });
  // Switching pattern keeps the routine's "Starting from" day.
  const savePattern = (next) =>
    save(rule?.startDate ? { ...next, startDate: rule.startDate } : next);

  const choosePreset = (preset) => {
    if (preset === "none") return save(null);
    if (preset === "weekdays") {
      return savePattern({ freq: "weekly", interval: 1, weekdays: WORKDAYS });
    }
    if (preset === "weekly") {
      // Start from the weekday the task is due on (or today).
      const day = parseLocalDate(task.dueDate || todayString()).getDay();
      return savePattern({ freq: "weekly", interval: 1, weekdays: [day] });
    }
    return savePattern({ freq: preset, interval: 1 });
  };

  const commitInterval = () => {
    const n = Number(intervalDraft);
    if (!Number.isInteger(n) || n < 1 || n > 365) {
      setIntervalDraft(String(rule.interval));
      return;
    }
    if (n !== rule.interval) save({ ...rule, interval: n });
  };

  const toggleDay = (day) => {
    const days = rule.weekdays.includes(day)
      ? rule.weekdays.filter((d) => d !== day)
      : [...rule.weekdays, day];
    if (days.length) save({ ...rule, weekdays: days });
  };

  const preset = presetOf(rule);
  const unit = rule && UNITS[rule.freq];

  return (
    <div className="tk-repeat">
      <select
        id="tk-repeat"
        className="tk-input"
        value={preset}
        onChange={(e) => choosePreset(e.target.value)}
      >
        <option value="none">Doesn't repeat</option>
        <option value="daily">Daily</option>
        <option value="weekdays">Every weekday (Mon–Fri)</option>
        <option value="weekly">Weekly on chosen days</option>
        <option value="monthly">Monthly</option>
      </select>

      {rule && preset !== "weekdays" && (
        <div className="tk-repeat-row">
          <span className="tk-muted">Every</span>
          <input
            type="number"
            min={1}
            max={365}
            className="tk-input tk-repeat-interval"
            aria-label={`Repeat every how many ${unit}s`}
            value={intervalDraft}
            onChange={(e) => setIntervalDraft(e.target.value)}
            onBlur={commitInterval}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          />
          <span className="tk-muted">
            {unit}
            {Number(intervalDraft) === 1 ? "" : "s"}
            {rule.freq === "monthly" &&
              task.dueDate &&
              ` on day ${parseLocalDate(task.dueDate).getDate()}`}
          </span>
        </div>
      )}

      {rule?.freq === "weekly" && preset !== "weekdays" && (
        <div className="tk-repeat-days" role="group" aria-label="Repeat on">
          {WEEKDAYS.map((w) => {
            const on = rule.weekdays.includes(w.day);
            return (
              <button
                key={w.day}
                type="button"
                className={`tk-repeat-day ${on ? "on" : ""}`.trim()}
                aria-pressed={on}
                aria-label={w.name}
                title={on && rule.weekdays.length === 1 ? "Pick another day first" : w.name}
                onClick={() => toggleDay(w.day)}
              >
                {w.short}
              </button>
            );
          })}
        </div>
      )}

      {rule && (
        <div className="tk-repeat-row">
          <label className="tk-muted" htmlFor="tk-repeat-start">
            Starting from
          </label>
          <input
            id="tk-repeat-start"
            type="date"
            className="tk-input"
            min={todayString()}
            value={rule.startDate || task.dueDate || ""}
            onChange={(e) =>
              e.target.value && save({ ...rule, startDate: e.target.value })
            }
          />
        </div>
      )}

      {task.nextOccurrence ? (
        <button
          type="button"
          className="tk-link-btn"
          onClick={() => onOpenTask(task.nextOccurrence.id)}
        >
          Open next repeat (due {formatDueDate(task.nextOccurrence.dueDate)}) →
        </button>
      ) : (
        rule && (
          <p className="tk-muted tk-repeat-note">
            Due {formatDueDate(task.dueDate)}. Completing it creates the next
            one on the following repeat day.
          </p>
        )
      )}
    </div>
  );
}
